import { addToCart } from "../redux/cartRedux";
import { requestCatalogCartLine } from "../api/catalogCartLine";
import { cartOutletId, withOutletQuery } from "./vendorOutlets";
import { productPath, shopPath } from "./seoPaths";
import { isQuoteOnly } from "./priceOnRequest";
import { vendorCartKey, vendorLineAddActions } from "./vendorCartLines";
import { CART_KINDS, cartLineQuantity } from "./cartLineSteps";

// ─────────────────────────────────────────────────────────────────────────────
// "Unified browse, segregated carts"
//
// The /shop grid merges every vendor type for DISCOVERY, while the platform
// keeps a separate cart and checkout per vendor (each enforces its own COD,
// minimum order, tier pricing and delivery rules). A card's button puts the
// product in ITS vendor's cart without leaving the grid (2 Nov 2026). It used
// to send every shop and brand product to the vendor's storefront ("Store"),
// where the item the shopper had picked was nowhere in sight.
//
// What the button does is the server's call, made once per row
// (`product.purchase`, services/catalog/catalogPurchase.js):
//
//   ADD          into the vendor's cart from the grid. Lusuku's own stock goes
//                straight into the in-house cart; a shop or brand product is
//                built into a line by POST /api/catalog/cart-line, priced there
//   CHOOSE       a size or other choice first: opens the product's own page
//   QUOTE        price on request: opens the product's own page, where the
//                quote is asked for and its progress shown
//   UNAVAILABLE  sold out or never priced: the button is disabled
//
// The picture and the name open the product's own page; the vendor's name
// opens its storefront.
// ─────────────────────────────────────────────────────────────────────────────

export const CATALOG_ACTIONS = Object.freeze({
  ADD: "ADD",
  CHOOSE: "CHOOSE",
  QUOTE: "QUOTE",
  UNAVAILABLE: "UNAVAILABLE",
});

export const isInhouse = (p) => p?.vendorType === "inhouse";

const isShop = (p) => p?.vendorType === "retailer" || p?.vendorType === "wholesaler";
const isBrand = (p) => p?.vendorType === "brand";

/**
 * The card's action. The server's verdict when the row carries one; an older
 * response is read the way the card always read it (quote, sold out, else add),
 * and the add endpoint still sends a product with choices to its page.
 */
export const catalogActionOf = (product) => {
  const verdict = product?.purchase?.action;
  if (verdict && CATALOG_ACTIONS[verdict]) return verdict;
  if (isQuoteOnly(product)) return CATALOG_ACTIONS.QUOTE;
  return Number(product?.countInStock) > 0 ? CATALOG_ACTIONS.ADD : CATALOG_ACTIONS.UNAVAILABLE;
};

/** How many one tap adds: the product's minimum order, 1 when it has none. */
export const catalogMinQty = (product) => Math.max(1, Math.round(Number(product?.purchase?.minQty) || 1));

/** The words on the card's button. */
export const catalogActionLabel = (product) => {
  switch (catalogActionOf(product)) {
    case CATALOG_ACTIONS.CHOOSE:
      return "See options";
    case CATALOG_ACTIONS.QUOTE:
      return "Request quote";
    case CATALOG_ACTIONS.UNAVAILABLE:
      return Number(product?.countInStock) > 0 ? "Not available" : "Sold out";
    default:
      return "Add to cart";
  }
};

/* The vendor as the address builders (utils/seoPaths.js) read it. A brand's
   catalogue name is the name its storefront shows. */
const catalogVendorOf = (product) => ({
  _id: product?.vendorId,
  name: product?.vendorName || "",
  tradingName: product?.vendorName || "",
});

// The storefront a shop or brand product's vendor name opens, or null.
// One builder, so the tap and the link search engines follow name one page.
export const catalogStorePath = (product) => {
  if (!product || !product.vendorId) return null;
  if (isShop(product)) return shopPath("retail", catalogVendorOf(product));
  if (isBrand(product)) {
    // A product listed under a brand OUTLET opens that outlet's storefront
    // (`?outletId=`), so the hours, coverage and fee the shopper then sees,
    // and the outlet every cart line names, are the outlet's.
    const path = shopPath("brand", catalogVendorOf(product));
    return path ? withOutletQuery(path, product.outletId || null) : null;
  }
  return null;
};

/** The product's own page, where it can be seen whole, sized and quoted. */
export const catalogProductPath = (product) => {
  if (!product?._id) return null;
  if (isInhouse(product)) return `/product/${product._id}`;
  if (!product.vendorId) return null;
  if (isShop(product)) return productPath("retail", catalogVendorOf(product), product);
  if (isBrand(product)) {
    const path = productPath("brand", catalogVendorOf(product), product);
    return path ? withOutletQuery(path, product.outletId || null) : null;
  }
  return null;
};

/** The cart hub group a product's lines sit in (utils/cartLineSteps.js). */
export const catalogCartKind = (product) => {
  if (isInhouse(product)) return CART_KINDS.INHOUSE;
  if (isShop(product)) return CART_KINDS.RETAILER;
  if (isBrand(product)) return CART_KINDS.BRAND;
  return null;
};

/** This product's lines in its vendor's cart, read from the store's state. Pure. */
export const catalogCartLines = (state, product) => {
  const id = String(product?._id || "");
  if (!id) return [];
  if (isInhouse(product)) return (state?.cart?.products || []).filter((line) => String(line?._id) === id);
  const vendorId = String(product?.vendorId || "");
  if (isShop(product)) {
    const line = state?.retailerWholesalerCart?.carts?.[vendorId]?.[id];
    return line ? [line] : [];
  }
  if (isBrand(product)) {
    return Object.values(state?.brandCart?.carts?.[vendorId] || {}).filter((line) => String(line?._id) === id);
  }
  return [];
};

/** How many of this product are in its vendor's cart, every size together. */
export const catalogCartQuantity = (state, product) =>
  catalogCartLines(state, product).reduce((sum, line) => sum + cartLineQuantity(line), 0);

/**
 * The one line the card's quantity control edits: the product's plain line,
 * with no size, choice or option picked. A product sold in sizes is changed
 * in the cart or on its page instead, where the size shows.
 */
export const catalogPlainLine = (state, product) => {
  const lines = catalogCartLines(state, product);
  if (!isBrand(product)) return lines[0] || null;
  return state?.brandCart?.carts?.[String(product.vendorId)]?.[String(product._id)] || null;
};

/**
 * Put a catalogue product in its vendor's cart (a thunk). Resolves to what
 * the card needs next:
 *   { action, added, vendorName, quantity, price, reason }
 * `added` is true only when a line went in; otherwise `action` says what to
 * do instead (CHOOSE or QUOTE open the product page) and `reason` why not.
 * A network failure rejects.
 */
export const addCatalogProductToCart = (product) => async (dispatch, getState) => {
  if (!product?._id) return { action: CATALOG_ACTIONS.UNAVAILABLE, added: false };

  if (isInhouse(product)) {
    dispatch(
      addToCart({
        item: {
          _id: product._id,
          title: product.name,
          price: product.price,
          product_image: product.image || product.product_image,
          countInStock: product.countInStock,
          creator: product.vendorId,
          unit_id:
            product.unit_id && product.unit_id._id
              ? product.unit_id
              : { _id: null, unit: product.unit_id?.unit || "" },
        },
      })
    );
    return { action: CATALOG_ACTIONS.ADD, added: true, vendorName: "Lusuku", quantity: 1, price: product.price };
  }

  // A brand cart is fulfilled from ONE branch, so a line joins the branch the
  // cart is already at and is priced there.
  const brandCart = isBrand(product) ? getState()?.brandCart?.carts?.[String(product.vendorId)] : null;
  const answer = await requestCatalogCartLine({
    productId: product._id,
    outletId: brandCart ? cartOutletId(brandCart) : null,
  });
  const action = CATALOG_ACTIONS[answer?.action] ? answer.action : CATALOG_ACTIONS.UNAVAILABLE;
  const outcome = {
    action,
    added: false,
    vendorName: answer?.vendorName || product.vendorName || "",
    quantity: 0,
    price: null,
    reason: answer?.reason || null,
  };
  if (action !== CATALOG_ACTIONS.ADD || !answer?.line) return outcome;

  const actions = vendorLineAddActions(answer.vertical, vendorCartKey(answer), answer.line, {
    outletId: answer.outletId || null,
  });
  actions.forEach((cartAction) => dispatch(cartAction));
  return {
    ...outcome,
    added: actions.length > 0,
    quantity: Number(answer.line.quantity) || 1,
    price: answer.line.product?.price ?? null,
  };
};

// Human label for a vendor type (badge text fallback).
export const vendorTypeLabel = (vt) =>
  ({
    inhouse: "Lusuku",
    retailer: "Retailer",
    wholesaler: "Wholesaler",
    brand: "Brand",
  }[vt] || "Vendor");
